// The task-group page's Toolchains card (D90): what the group's own worker image adds to the
// platform's (Rust, Go, Java, and an optional root setup script), and how its build went:
// building (the next task waits), ready, or failed with the build's output and Rebuild. The
// owner edits it; the workflow's toolchain requirements are marked. A new script needs the
// CustomiseWorkerImage permission.
import { Wrench } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import { accessQuery } from "../../auth/access";
import { usePermissions } from "../../auth/permissions";
import type { Toolchain } from "../../catalog/types";
import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { CollapsibleCard } from "../../ui/CollapsibleCard";
import { ErrorState, Loading } from "../../ui/states";
import { Chip, PulseDot, SummaryRow } from "../../ui/summary";
import { groupRequirementsQuery } from "../api";
import type { TaskGroup } from "../model";
import { presetsOf, setToolchains, type ToolchainsDto, toolchainsQuery } from "../toolchains/api";
import { ImageStatus } from "../toolchains/ImageStatus";
import { ToolchainsEditor } from "../toolchains/ToolchainsEditor";

function Summary({ dto }: { dto: ToolchainsDto }) {
  const { tasks, workflows } = useT();
  const t = tasks.toolchains;
  const presets = presetsOf(dto);
  const state = dto.image?.state;
  return (
    <SummaryRow>
      {state === "building" ? <PulseDot tone="blue" label={t.state.building} /> : null}
      {state === "failed" ? <Chip tone="red">{t.state.failed}</Chip> : null}
      {presets.length === 0 && !dto.script ? <Chip tone="gray">{t.none}</Chip> : null}
      {presets.map((p) => (
        <Chip key={p} tone="primary">
          {workflows.needs.requirements.toolchainNames[p].split(" ")[0]}
        </Chip>
      ))}
      {dto.script ? <Chip tone="gray">{t.scriptChip}</Chip> : null}
    </SummaryRow>
  );
}

function Editor({ group, dto, required }: { group: TaskGroup; dto: ToolchainsDto; required: Toolchain[] }) {
  const t = useT().tasks.toolchains;
  const queryClient = useQueryClient();
  const mayScript = usePermissions().can("CustomiseWorkerImage");
  const [presets, setPresets] = useState(presetsOf(dto));
  const [script, setScript] = useState(dto.script ?? "");
  const [busy, setBusy] = useState(false);
  const saved = presetsOf(dto);
  const changed = presets.length !== saved.length || presets.some((p) => !saved.includes(p)) || script.trim() !== (dto.script ?? "").trim();
  const missing = required.filter((r) => !presets.includes(r));

  async function save() {
    setBusy(true);
    try {
      const next = await setToolchains(group.id, presets, script.trim() ? script : null);
      queryClient.setQueryData(toolchainsQuery(group.id).queryKey, next);
      await queryClient.invalidateQueries({ queryKey: ["task-groups", group.id, "requirements"] });
      toast(t.saved);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <ToolchainsEditor
        presets={presets}
        onPresets={setPresets}
        required={required}
        script={script}
        onScript={setScript}
        scriptShown={mayScript || Boolean(dto.script)}
        scriptLocked={!mayScript && Boolean(dto.script)}
      />
      {missing.length > 0 ? <p className="m-0 text-[13px] text-orange-dark">{t.missingRequired}</p> : null}
      <div className="flex items-center justify-between gap-3">
        <p className="m-0 text-xs text-gray-500">{t.appliesNext}</p>
        <Button size="sm" onClick={() => void save()} isLoading={busy} disabled={!changed}>
          {t.save}
        </Button>
      </div>
    </div>
  );
}

export function ToolchainsCard({ group }: { group: TaskGroup }) {
  const t = useT().tasks.toolchains;
  const me = useQuery(accessQuery()).data;
  const toolchains = useQuery(toolchainsQuery(group.id));
  const requirements = useQuery(groupRequirementsQuery(group.id));
  const owner = me?.id === group.owner_user_id;
  const required = (requirements.data?.requirements ?? []).flatMap((r) => (r.requirement.kind === "toolchain" ? [r.requirement.toolchain] : []));
  return (
    <CollapsibleCard
      kind="toolchains"
      icon={<Wrench weight="duotone" />}
      title={t.title}
      description={t.description}
      summary={toolchains.data ? <Summary dto={toolchains.data} /> : null}
    >
      {toolchains.isError ? <ErrorState title={t.loadFailed} message={toolchains.error.message} /> : null}
      {toolchains.isPending ? <Loading /> : null}
      {toolchains.data ? (
        <div className="space-y-5">
          <ImageStatus groupId={group.id} image={toolchains.data.image ?? null} canRebuild={owner} />
          {owner ? (
            <Editor key={JSON.stringify([toolchains.data.presets, toolchains.data.script])} group={group} dto={toolchains.data} required={required} />
          ) : (
            <p className="m-0 text-[13px] text-gray-600">{presetsOf(toolchains.data).join(", ") || t.none}</p>
          )}
        </div>
      ) : null}
    </CollapsibleCard>
  );
}
